import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
import { ConnectionState } from '@core/devices/IDevice'
import type { SearchResultListItem } from '@/core/search/SearchProvider'
import { SearchProvider } from '@/core/search/SearchProvider'
import { INSTANCE as ApplicationManager } from '@core/application/ApplicationManager'
import { HttpRequestCancelToken } from '@core/HttpRequestCancelToken'
import { AppCategory } from '@core/application/ApplicationConstants'

const DEFAULT_CURRENT_PAGE = 1
const DEFAULT_PAGE_SIZE = 15
const PROVIDER_TYPE = AppCategory.FILE
const SEARCH_SERVICE_ID = '2000011'
const SEARCH_TYPE = 'simpleKeyword'
const SEARCH_FIELD = 'namePinyin'
const RESOURCE = 'file'
const CALLINGAPP = 'owork'
const FILTERS: Array<{ method: string; field: string; value: string }> = [
  { method: 'HiddenFileFilter', field: 'briefHide', value: 'true' },
]
const SORTBY = 'relativity DESC&lastModified DESC'

interface SearchResultPageData {
  pages: number
  totalNum: number
  indexStatus: number
  pageNum: number
}

interface SearchResultList {
  outParams: SearchResult
}

interface SearchResult {
  contentProviderResponse?: SearchResultListItem[]
  contentProviderExtraData?: SearchResultPageData
}

interface SearchParam {
  searchType: string
  searchField: string
  keyword: string
  pageNum: number
  pageSize: number
  resource: string
  callingApp: string
  filters: string
  sortBy: string
}

interface SearchStateChangeListener {
  onDeviceConnectionStateChanged(id: string): void

  onDeviceRemoved(id: string): void
}

export class DeviceSearchProvider extends SearchProvider {
  #isConnected: boolean
  #notifyStateChanged: SearchStateChangeListener
  #params: SearchParam = {
    searchType: SEARCH_TYPE,
    searchField: SEARCH_FIELD,
    keyword: '',
    pageNum: DEFAULT_CURRENT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    resource: RESOURCE,
    callingApp: CALLINGAPP,
    filters: JSON.stringify(FILTERS),
    sortBy: SORTBY,
  }

  #listener = {
    onInitialized: () => {
      // do nothing
    },
    onDeviceListUpdated: async () => {
      await this.#updateDeviceSearchProvider()
    },
    onCurrentSelectedDeviceChanged: () => {
      // do nothing
    },
  }

  constructor(id: string, isConnected: boolean, notifySearchListChanged: SearchStateChangeListener, appId: string) {
    super()
    this._id = id
    this.#isConnected = isConnected
    this.#notifyStateChanged = notifySearchListChanged
    this._source = PROVIDER_TYPE
    this._sourceName = ApplicationManager.getCategoryNameByType(this._source)
    this._appId = appId

    const device = DeviceManager.getDeviceById(id)

    if (device) {
      this._name = device.model
      DeviceManager.addChangeListener(this.#listener)
    }
  }

  async destroy() {
    await this._clean()
    DeviceManager.removeChangeListener(this.#listener)
  }

  getConnectState(): boolean {
    return this.#isConnected
  }

  async #updateDeviceSearchProvider() {
    const device = DeviceManager.getDeviceById(this._id)

    if (device) {
      const isConnected = device.connectionState === ConnectionState.CONNECTED

      if (this.#isConnected !== isConnected) {
        this.#isConnected = isConnected

        if (isConnected) {
          await this.search()
        } else {
          await this._clean()
        }

        this.#notifyStateChanged.onDeviceConnectionStateChanged(this._id)
      }
    } else {
      this.#notifyStateChanged.onDeviceRemoved(this._id)
    }
  }

  init(keyword: string): void {
    this.#params.keyword = keyword
  }

  async search() {
    const device = DeviceManager.getDeviceById(this._id)

    if (device) {
      try {
        this._requestCancelToken = new HttpRequestCancelToken()

        this.#params.pageNum = this.currentPageNum

        const res = await device.callService(
          SEARCH_SERVICE_ID,
          {
            params: this.#params,
          },
          {},
          this._requestCancelToken
        )

        // 由于搜索中台是预装不可卸载的，所以这里的调用不会出现statuscode为500的情况，故不需要处理res.data.statusCode
        if (res) {
          const searchLists = (res.data.data as SearchResultList).outParams
          const { contentProviderExtraData, contentProviderResponse } = searchLists

          this._setInfo(contentProviderResponse, contentProviderExtraData?.pages)
        } else {
          this.hasMorePages = false
        }
      } catch (error) {
        this.hasMorePages = false
      }
    } else {
      this.hasMorePages = false
    }
  }
}
